/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback, useMemo } from "react";

import { SeriesRecordingOptionsOverlayController } from "./SeriesRecordingOptionsOverlayController";
import { HorizontalSelectionDialogContainer } from "./HorizontalSelectionDialog";
import { ListSelectionDialogContainer } from "./ListSelectionDialog";
import SeriesRecordingOptionsOverlay from "./SeriesRecordingOptionsOverlay";
import {
  useUnityDispatch,
  useUnitySelector,
  UnityAppStore
} from "../../../viewModel";
import {
  selectIsSeriesRecordingOptionsVisible,
  selectSeriesRecordingOptionsTitle,
  selectSeriesRecordingOptions,
  selectSeriesRecordingSaveLabel,
  selectSeriesRecordingCancelLabel,
  selectSeriesRecordingFocus,
  selectActiveRecordingOption,
  RecordingOptionListType,
  openOptionValue,
  cycleOptionValue,
  RecordingOption,
  setFocus,
  saveSeriesRecordingOptions,
  dismissSeriesRecordingOptions
} from "../../../viewModel/myHub/seriesRecordingOptions";
import { useModalController } from "../../hooks";

/**
 * Connected container that reads Series Recording Options state from Redux and
 * renders the overlay when visible.
 *
 * Pushes a SeriesRecordingOptionsOverlayController onto the active screen's
 * modal stack so all D-pad input is consumed by the overlay while it is open.
 *
 * Dialog selection is handled here because it is a presentation concern:
 * - Channel uses ListSelectionDialog.
 * - KeepAtMost, StartRecording and StopRecording use
 *   HorizontalSelectionDialog.
 * - Remaining option types toggle their value directly.
 */
const SeriesRecordingOptionsOverlayContainer: FunctionComponent =
  (): JSX.Element | null => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
    const TAG: string = "SeriesRecordingOptionsOverlayContainer";
    const dispatch = useUnityDispatch();

    const isVisible = useUnitySelector(selectIsSeriesRecordingOptionsVisible);
    const title = useUnitySelector(selectSeriesRecordingOptionsTitle);
    const options = useUnitySelector(selectSeriesRecordingOptions);
    const saveButtonLabel = useUnitySelector(selectSeriesRecordingSaveLabel);
    const cancelButtonLabel = useUnitySelector(
      selectSeriesRecordingCancelLabel
    );
    const focus = useUnitySelector(selectSeriesRecordingFocus);
    const activeRecordingOption = useUnitySelector(selectActiveRecordingOption);

    const modalController = useMemo(
      (): SeriesRecordingOptionsOverlayController =>
        new SeriesRecordingOptionsOverlayController(
          dispatch,
          UnityAppStore.getState
        ),
      [dispatch]
    );

    useModalController(modalController, isVisible);

    const handleOptionActivate = useCallback(
      (optionListType: RecordingOptionListType): void => {
        switch (optionListType) {
          case RecordingOptionListType.Channel:
          case RecordingOptionListType.KeepAtMost:
          case RecordingOptionListType.StartRecording:
          case RecordingOptionListType.StopRecording:
            dispatch(openOptionValue(optionListType));
            break;

          default:
            dispatch(
              cycleOptionValue({
                optionListType,
                direction: "next"
              })
            );
            break;
        }
      },
      [dispatch]
    );

    const handleOptionFocus = useCallback(
      (optionListType: RecordingOptionListType): void => {
        const index = options.findIndex(
          (option: RecordingOption): boolean =>
            option.optionListType === optionListType
        );

        if (index >= 0) {
          dispatch(setFocus({ zone: "rows", index }));
        }
      },
      [dispatch, options]
    );

    const handleSave = useCallback((): void => {
      dispatch(saveSeriesRecordingOptions());
    }, [dispatch]);

    const handleCancel = useCallback((): void => {
      dispatch(dismissSeriesRecordingOptions("cancel"));
    }, [dispatch]);

    const handleActionFocus = useCallback(
      (index: number): void => {
        dispatch(setFocus({ zone: "actions", index }));
      },
      [dispatch]
    );

    if (isVisible !== true) {
      return null;
    }

    const activeOptionListType =
      activeRecordingOption !== undefined
        ? activeRecordingOption.optionListType
        : "";

    const isListSelectionDialogVisible =
      activeOptionListType === RecordingOptionListType.Channel;

    const isHorizontalSelectionDialogVisible =
      activeOptionListType === RecordingOptionListType.KeepAtMost ||
      activeOptionListType === RecordingOptionListType.StartRecording ||
      activeOptionListType === RecordingOptionListType.StopRecording;

    return (
      <>
        <SeriesRecordingOptionsOverlay
          title={title}
          options={options}
          saveButtonLabel={saveButtonLabel}
          cancelButtonLabel={cancelButtonLabel}
          focus={focus}
          onOptionActivate={handleOptionActivate}
          onOptionFocus={handleOptionFocus}
          onSave={handleSave}
          onCancel={handleCancel}
          onActionFocus={handleActionFocus}
        />

        {isHorizontalSelectionDialogVisible ? (
          <HorizontalSelectionDialogContainer />
        ) : null}

        {isListSelectionDialogVisible ? <ListSelectionDialogContainer /> : null}
      </>
    );
  };

export default SeriesRecordingOptionsOverlayContainer;
